import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'

import * as SimpleForm from 'builder/components/SimpleForm'
import SelectResumeModal from 'builder/views/ResumeDistributionView/components/SelectResumeModal'

import { BriefDocumentType } from 'builder/modules/panel'
import { DocumentTypes } from 'builder/modules/constants'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { selectors } from 'builder/modules/autoApply/autoApplyModule'

import { useI18n } from 'builder/hooks/useI18n'
import { useDocument } from 'builder/hooks/useDocument'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { usePrompt } from '../../hooks/usePrompt'
import { usePatchForm } from '../../hooks/usePatchForm'
import { useStepsConfig } from '../../hooks/useStepsConfig'

import { AA_TAILOR_RESUME_CHECK_TEMP_KEY, Routes, TotalPageCount } from '../../constants'
import { Container, FillWarning, LayoutContainer, Title, TitleWrapper } from '../styles'
import { ConfirmModal } from '../ConfirmModal/ConfirmModal'
import { CoverLetterForm } from './CoverLetterForm'
import { FormContainer } from './styles'

const TRANSLATION = 'builder.auto_apply.form.cover_letter'
export type TailorResume = Pick<
  AutoApplyForm,
  'autoTailoring' | 'autoTailoringCl' | 'selectedCoverLetterId'
>

export const CoverLetterContainer = () => {
  const [showResumeSelect, setShowResumeSelect] = useState(false)

  const { i18n } = useI18n()
  const navigate = useNavigate()
  const stepsConfig = useStepsConfig()
  const formState = useSelector(selectors.form)
  const { features } = useFeaturesConfig()

  const trNewFlow = features.aaTailoring === 'v1'

  const trDocument = useDocument(
    formState.resumeType === DocumentTypes.trDocument ? formState.resumeId : null,
  )

  const autoTailorTempValue = () => {
    const state = localStorage.getItem(AA_TAILOR_RESUME_CHECK_TEMP_KEY)
    return state ? state === 'true' : undefined
  }

  const getDefaultValue = () => {
    if (autoTailorTempValue() === undefined) {
      return formState.autoTailoring
    }
    return autoTailorTempValue()
  }

  const form = useForm<TailorResume>({
    mode: 'onChange',
    defaultValues: { autoTailoring: getDefaultValue(), autoTailoringCl: formState.autoTailoringCl },
  })
  const { mutateAsync: patchForm, isLoading } = usePatchForm()
  const { isPromptVisible, handleCancel, handleConfirm } = usePrompt({
    when: true,
    ignoreParentRoute: ['auto-apply', 'preview', 'tr-documents'],
  })

  const onBuilderCoverLetterSelect = async (resumeDetails: BriefDocumentType) => {
    form.setValue('selectedCoverLetterId', resumeDetails.id)
    await patchForm({ selectedCoverLetterId: resumeDetails.id })
  }

  const onNextStep = async () => {
    const payload = form.getValues()
    await patchForm(payload)

    trackInternalEvent('cover_letter_toggle', {
      label: payload.autoTailoringCl ? 'on' : 'off',
    })

    if (formState.autoTailoring && trDocument && !trNewFlow) {
      const path = `?path=${Routes.COVER_LETTER}&mode=preview_tailoring`
      navigate(`/documents/${trDocument?.resume_id}/${DocumentTypes.resume}/preview${path}`)
    } else {
      navigate(`/auto-apply/${stepsConfig[Routes.COVER_LETTER].next}`)
    }
  }

  const onBackClick = () => {
    form.clearErrors()
    navigate(`/auto-apply/${stepsConfig[Routes.COVER_LETTER].prev}`)
  }
  const disableNext = Object.keys(form.formState.errors).length > 0

  return (
    <LayoutContainer>
      <Container>
        <TitleWrapper>
          <Title>{i18n.t(`${TRANSLATION}.title`)}</Title>
          <FillWarning>{i18n.t(`${TRANSLATION}.sub_title`)}</FillWarning>
        </TitleWrapper>
        <FormContainer>
          <SimpleForm.Form
            submitButtonText={i18n.t(`${TRANSLATION}.next`)}
            onBackClick={onBackClick}
            onSubmit={form.handleSubmit(onNextStep)}
            isPending={isLoading}
            isDisabled={disableNext}
            processIndicator={{
              totalStep: TotalPageCount,
              currentStep: stepsConfig[Routes.COVER_LETTER].order,
            }}
          >
            <CoverLetterForm form={form} setShowResumeSelect={setShowResumeSelect} />
          </SimpleForm.Form>
        </FormContainer>
      </Container>
      {isPromptVisible && <ConfirmModal onClose={handleCancel} onConfirm={handleConfirm} />}
      {showResumeSelect && (
        <SelectResumeModal
          isTotalJobSearchFlow
          title={i18n.t(`${TRANSLATION}.select_cover_letter`)}
          viewOnly={DocumentTypes.trDocument}
          trFilterType={DocumentTypes.coverLetter}
          onClose={() => setShowResumeSelect(false)}
          submitSelectedResume={onBuilderCoverLetterSelect}
        />
      )}
    </LayoutContainer>
  )
}
